import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

const mapPaymentGateways = (gateways) =>
  gateways?.map((gw) => ({
    id: gw.id,
    countryId: gw.country_id,
    gatewayName: gw.payment_gateway,
    currencyStatus: gw.currency_status === 'ACTIVE',
    createdAt: gw.created_at,
    updatedAt: gw.updated_at,
  })) || [];

const mapCountry = (country) => ({
  _id: country.id,
  countryName: country.country_name,
  countryCode: country.country_code,
  currencyName: country.currency_name,
  currencyCode: country.currency_code,
  currencySymbol: country.currency_symbol,
  image: country.image_url,
  status: country.status?.toLowerCase(),
  createdAt: country.created_at,
  updatedAt: country.updated_at,
  paymentGateways: mapPaymentGateways(country.payment_gateways),
});

export const getCountires = createAsyncThunk('GET COUNTRIES', async (data, thunkAPI) => {
  let query = `?page=${data.page}&limit=${data.limit}&text=${data.text}`;

  const response = await axiosAdmin.get(`${URL.COUNTRY}${query}`, thunkAPI);
  const raw = response.data?.data;

  return {
    ...raw,
    data: {
      countries: raw.data?.map(mapCountry) || [],
      total: raw?.total_count ?? raw?.total ?? 0,
    },
  };
});

const GATEWAY_NAMES = ['STRIPE', 'WIPAY', 'FINMONT', 'AIRWALLEX'];

export const addCountry = createAsyncThunk('ADD COUNTRY', async (data, thunkAPI) => {
  const payload = {
    country_name: data.countryName,
    country_code: data.countryCode,
    currency_name: data.currencyName,
    currency_code: data.currencyCode,
    currency_symbol: data.currencySymbol,
    image_url: data.image,
    status: data.status ? 'ACTIVE' : 'INACTIVE',
    payment_gateways: GATEWAY_NAMES.map((name) => ({
      payment_gateway: name,
      currency_status: data.paymentCurrencyActive ? 'ACTIVE' : 'INACTIVE',
    })),
  };
  const response = await axiosAdmin.post(URL.COUNTRY, payload, thunkAPI);
  return response.data;
});

export const updateCountry = createAsyncThunk('UPDATE COUNTRY', async (data, thunkAPI) => {
  const payload = { id: data._id };

  if (data.gatewayName) {
    // Single gateway toggle from the table row
    payload.payment_gateways = [
      {
        payment_gateway: data.gatewayName,
        currency_status: data.currencyStatus ? 'ACTIVE' : 'INACTIVE',
      },
    ];
  } else {
    // Full update from the modal form
    if (data.countryName) payload.country_name = data.countryName;
    if (data.countryCode) payload.country_code = data.countryCode;
    if (data.currencyName) payload.currency_name = data.currencyName;
    if (data.currencyCode) payload.currency_code = data.currencyCode;
    if (data.currencySymbol) payload.currency_symbol = data.currencySymbol;
    if (data.image) payload.image_url = data.image;
    if (data.status !== undefined) {
      payload.status = data.status === true || data.status === 'active' ? 'ACTIVE' : 'INACTIVE';
    }
  }

  const response = await axiosAdmin.patch(URL.COUNTRY, payload, thunkAPI);
  return response.data;
});

export const deleteCountry = createAsyncThunk('DELETE COUNTRY', async (data, thunkAPI) => {
  const response = await axiosAdmin.delete(URL.COUNTRY, { id: data }, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  countriesData: null,
};

export const countriesSlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {},
  extraReducers: {
    [getCountires.pending]: (state) => {
      state.isLoading = true;
      state.countriesData = [];
    },
    [getCountires.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.countriesData = action.payload.data;
    },
    [getCountires.rejected]: (state, action) => {
      state.isLoading = false;
      state.countriesData = [];
      toast.error(action?.payload?.message);
    },

    [addCountry.pending]: (state) => {
      state.isLoading = true;
    },
    [addCountry.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload.message);
    },
    [addCountry.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateCountry.pending]: (state) => {
      state.isLoading = true;
    },
    [updateCountry.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload.message);
    },
    [updateCountry.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },

    [deleteCountry.pending]: (state) => {
      state.isLoading = true;
    },
    [deleteCountry.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload.message);
    },
    [deleteCountry.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },
  },
});

export default countriesSlice.reducer;
